<template>
  <div class="slide slide-who5-breakdown">
    <!-- Header -->
    <div class="slide-header">
      <h2 class="slide-title">{{ data.title }}</h2>
      <div v-if="data.subtitle" class="slide-subtitle">{{ data.subtitle }}</div>
    </div>

    <!-- Questions Grid -->
    <div class="questions-container">
      <div
        v-for="(question, index) in data.questions"
        :key="index"
        class="question-row"
        :style="{ animationDelay: `${index * 0.1}s` }"
      >
        <!-- Question Label -->
        <div class="question-label">{{ question.label }}</div>

        <!-- Stacked Bar -->
        <div class="bar-container">
          <div class="stacked-bar">
            <div
              v-for="(segment, segIndex) in getSegments(question.distribution)"
              :key="segIndex"
              class="bar-segment"
              :class="segment.colorClass"
              :style="{ width: segment.width + '%' }"
              :title="`${segment.label}: ${segment.count} responses`"
            ></div>
          </div>
          <div class="bar-labels">
            <span class="label-poor">Poor</span>
            <span class="label-good">Good</span>
          </div>
        </div>

        <!-- Score & Change -->
        <div class="score-container">
          <div class="score-value">{{ question.score }}</div>
          <div class="score-change" :class="getChangeClass(question.trend)">
            <svg v-if="question.trend === 'up'" class="trend-icon" width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M12 5L5 12H9V19H15V12H19L12 5Z" fill="currentColor"/>
            </svg>
            <svg v-else-if="question.trend === 'down'" class="trend-icon" width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M12 19L19 12H15V5H9V12H5L12 19Z" fill="currentColor"/>
            </svg>
            <span>{{ formatChange(question.change) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Legend -->
    <div class="legend">
      <div class="legend-item">
        <div class="legend-color legend-0"></div>
        <span>0 - At no time</span>
      </div>
      <div class="legend-item">
        <div class="legend-color legend-1"></div>
        <span>1 - Some of the time</span>
      </div>
      <div class="legend-item">
        <div class="legend-color legend-2"></div>
        <span>2 - Less than half</span>
      </div>
      <div class="legend-item">
        <div class="legend-color legend-3"></div>
        <span>3 - More than half</span>
      </div>
      <div class="legend-item">
        <div class="legend-color legend-4"></div>
        <span>4 - Most of the time</span>
      </div>
      <div class="legend-item">
        <div class="legend-color legend-5"></div>
        <span>5 - All of the time</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WHO5BreakdownSlide',
  props: {
    data: {
      type: Object,
      required: true
    },
    slideId: {
      type: String,
      default: ''
    }
  },
  methods: {
    getSegments(distribution) {
      if (!distribution) return [];

      // Calculate total responses
      const total = Object.values(distribution).reduce((sum, count) => sum + count, 0);
      if (total === 0) return [];

      // Individual segments for each score 0-5
      const segments = [
        { label: '0 - At no time', count: distribution[0] || 0, colorClass: 'segment-0' },
        { label: '1 - Some of the time', count: distribution[1] || 0, colorClass: 'segment-1' },
        { label: '2 - Less than half', count: distribution[2] || 0, colorClass: 'segment-2' },
        { label: '3 - More than half', count: distribution[3] || 0, colorClass: 'segment-3' },
        { label: '4 - Most of the time', count: distribution[4] || 0, colorClass: 'segment-4' },
        { label: '5 - All of the time', count: distribution[5] || 0, colorClass: 'segment-5' }
      ];

      // Calculate widths
      return segments.map(seg => ({
        ...seg,
        width: (seg.count / total) * 100
      }));
    },
    getChangeClass(trend) {
      if (trend === 'up') return 'change-positive';
      if (trend === 'down') return 'change-negative';
      return 'change-neutral';
    },
    formatChange(change) {
      if (change === 0 || change === undefined) return '—';
      const prefix = change > 0 ? '+' : '';
      return `${prefix}${change}`;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../../assets/quarterly-slides.scss';

.slide-who5-breakdown {
  @extend .slide-base;
  background: $paper;
  padding: 80px;
  display: flex;
  flex-direction: column;
}

.slide-header {
  margin-bottom: 48px;
}

.slide-title {
  @extend .serif-headline;
  font-size: 56px;
  color: $ink;
  margin: 0;
}

.slide-subtitle {
  font-family: $font-body;
  font-size: 20px;
  color: $ink-muted;
  margin-top: 12px;
}

.questions-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 28px;
}

.question-row {
  display: grid;
  grid-template-columns: 1fr 2fr 120px;
  gap: 32px;
  align-items: center;
  animation: fadeInUp 0.5s ease backwards;
}

.question-label {
  font-family: $font-body;
  font-size: 18px;
  color: $ink;
  line-height: 1.4;
}

.bar-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.stacked-bar {
  display: flex;
  height: 32px;
  border-radius: 16px;
  overflow: hidden;
  background: $paper-cool;
}

.bar-segment {
  height: 100%;
  transition: width 0.6s ease;

  // 6-point scale gradient from poor (red) to good (green)
  &.segment-0 {
    background: #E53935; // Deep red
  }

  &.segment-1 {
    background: #FB8C00; // Orange
  }

  &.segment-2 {
    background: #FDD835; // Yellow
  }

  &.segment-3 {
    background: #C0CA33; // Lime
  }

  &.segment-4 {
    background: #7CB342; // Light green
  }

  &.segment-5 {
    background: #43A047; // Green
  }
}

.bar-labels {
  display: flex;
  justify-content: space-between;
  font-family: $font-body;
  font-size: 12px;
  color: $ink-subtle;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.score-container {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.score-value {
  font-family: $font-body;
  font-size: 36px;
  font-weight: 700;
  color: $ink;
  line-height: 1;
}

.score-change {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: $font-body;
  font-size: 14px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 12px;

  &.change-positive {
    background: rgba($emerald, 0.1);
    color: $emerald;
  }

  &.change-negative {
    background: rgba($rose, 0.1);
    color: $rose;
  }

  &.change-neutral {
    background: rgba($ink, 0.05);
    color: $ink-muted;
  }

  .trend-icon {
    width: 14px;
    height: 14px;
  }
}

.legend {
  display: flex;
  justify-content: center;
  gap: 32px;
  margin-top: 40px;
  padding-top: 24px;
  border-top: 1px solid rgba($ink, 0.1);
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: $font-body;
  font-size: 14px;
  color: $ink-muted;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 4px;

  &.legend-0 {
    background: #E53935;
  }

  &.legend-1 {
    background: #FB8C00;
  }

  &.legend-2 {
    background: #FDD835;
  }

  &.legend-3 {
    background: #C0CA33;
  }

  &.legend-4 {
    background: #7CB342;
  }

  &.legend-5 {
    background: #43A047;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
